import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { resolveFindingMessage } from '@/shared/i18n/resolve-finding';
import { formatDateRange } from '@/shared/format/date-format';
import {
  TIER_ORDER,
  TIER_PRESENTATION,
  dedupeFindings,
  findingIdentity,
  findingsOfTier,
  isSubmitBlocked,
  submitBlockedReasonKey,
  type SummaryFinding,
  type SummaryTier,
} from './validation-model';
import { missingByStep, type MissingRequiredField } from './required-fields';
import { WIZARD_STEPS, type WizardStepId } from './wizard-steps';

/**
 * The Step 9 validation summary (task 23.2, UI spec §3.8 and §6.3).
 *
 * All four tiers are stacked in the fixed order hard → governance → warning →
 * information (§6.3). Each tier renders as the §3.8 alert anatomy: its exact
 * colours, a 1px border and the tier glyph prefix (`✕`/`!`/`⚠`/`ℹ`). A warning
 * carries an acknowledgement control (R8.5); an owner-inactive finding is
 * surfaced like any other finding through its `validation.owner.inactive`
 * message, and additionally flagged as an inactive-owner indication (R4.11).
 *
 * `Submit for review` is rendered with the blocked-button treatment while a hard
 * (or governance, or unacknowledged-warning) validation stands, but stays
 * **clickable**: clicking a blocked submit surfaces the reason rather than doing
 * nothing.
 */
export interface ValidationSummaryProps {
  readonly findings: readonly SummaryFinding[];
  /** Warnings of this evaluation still needing acknowledgement (R8.5). */
  readonly unacknowledgedWarningRuleIds: readonly string[];
  /** Acknowledge one warning rule (R8.5); lifts it from the unacknowledged set. */
  readonly onAcknowledgeWarning: (ruleId: string) => void;
  /** Save as Draft — permitted despite governance findings (R8.4). */
  readonly onSaveDraft: () => void;
  /** Submit for review — blocked styling while a hard validation stands; still clickable. */
  readonly onSubmit: () => void;
  /** Whether a server evaluation is currently in flight (the 300 ms-debounced call). */
  readonly isRevalidating?: boolean;
  /**
   * Required inputs still empty anywhere in the intake, with the step that owns
   * each. Computed locally so the advisory stands even before (or without) a
   * server evaluation. Empty when nothing is missing.
   */
  readonly missingFields?: readonly MissingRequiredField[];
  /** Jump to a step — how the advisory sends the user to the input that is missing. */
  readonly onNavigateToStep?: (step: WizardStepId) => void;
}

export function ValidationSummary({
  findings,
  unacknowledgedWarningRuleIds,
  onAcknowledgeWarning,
  onSaveDraft,
  onSubmit,
  isRevalidating = false,
  missingFields = [],
  onNavigateToStep,
}: ValidationSummaryProps): React.JSX.Element {
  const { t } = useTranslation();
  const [surfacedReasonKey, setSurfacedReasonKey] = useState<string | null>(null);

  const blocked = isSubmitBlocked(findings, unacknowledgedWarningRuleIds);
  const anyMissing = missingFields.length > 0;

  const handleSubmit = (): void => {
    if (blocked) {
      // Blocked but clickable: surface the reason rather than silently no-op.
      setSurfacedReasonKey(submitBlockedReasonKey(findings, unacknowledgedWarningRuleIds));
      return;
    }
    setSurfacedReasonKey(null);
    onSubmit();
  };

  const anyFinding = findings.length > 0;

  return (
    <div>
      <div
        className="flex flex-col gap-2"
        role="region"
        aria-label={t('validationSummary.regionLabel')}
        aria-busy={isRevalidating}
      >
        {/* Missing required inputs come first: they are the reason the rest of the
            summary may be thin (a rule cannot fail on a value that is not there
            yet), and they are the only block that offers a destination. */}
        {anyMissing ? (
          <MissingInputsAdvisory
            missingFields={missingFields}
            {...(onNavigateToStep ? { onNavigateToStep } : {})}
          />
        ) : null}

        {anyFinding
          ? TIER_ORDER.map((tier) => (
              <TierGroup
                key={tier}
                tier={tier}
                findings={findingsOfTier(findings, tier)}
                unacknowledgedWarningRuleIds={unacknowledgedWarningRuleIds}
                onAcknowledgeWarning={onAcknowledgeWarning}
              />
            ))
          : null}

        {/* "All clear" is claimed only when no rule failed *and* nothing required is
            still empty. A rule cannot fail on a value that is not there yet, so
            without the second condition an untouched intake would read as ready to
            submit — the very thing the advisory above exists to correct. */}
        {!anyFinding && !anyMissing ? (
          <p className="text-[12px] text-[#14724A]">{t('validationSummary.allClear')}</p>
        ) : null}
      </div>

      {surfacedReasonKey ? (
        <p className="mt-3 text-[12px] text-[#8E2A1E]" role="alert">
          {t(surfacedReasonKey)}
        </p>
      ) : null}

      {/* Footer actions of §6.3: Save draft · Submit for review (blocked styling
          while hard validations exist) · ghost Discard is the wizard's own reset. */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={onSaveDraft}>
          {t('validationSummary.saveDraft')}
        </Button>
        <Button
          size="sm"
          data-blocked={blocked ? 'true' : undefined}
          aria-disabled={blocked ? 'true' : undefined}
          className={
            blocked
              ? 'border border-[#F0C3BC] bg-transparent text-[#8E2A1E] hover:bg-[#FCEDEB]'
              : undefined
          }
          onClick={handleSubmit}
        >
          {t('validationSummary.submit')}
        </Button>
        {isRevalidating ? (
          <span className="text-muted-foreground text-[11px]" aria-live="polite">
            {t('validationSummary.revalidating')}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The missing-required-inputs advisory: one row per step that still has an empty
 * required input, each naming the fields and offering a jump to that step.
 *
 * Painted with the governance tier's own §3.8 tokens rather than a new palette —
 * an incomplete intake blocks the transition to Submitted exactly as a governance
 * finding does, so it belongs to that visual class and introduces no fifth alert
 * style. `role="alert"` because it appears in response to the user reaching this
 * step and changes what the summary above it means.
 */
function MissingInputsAdvisory({
  missingFields,
  onNavigateToStep,
}: {
  readonly missingFields: readonly MissingRequiredField[];
  readonly onNavigateToStep?: (step: WizardStepId) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const presentation = TIER_PRESENTATION.governance;
  const groups = missingByStep(missingFields);

  return (
    <section
      className={`${presentation.className} rounded-[var(--radius-alert,4px)] border p-2`}
      style={{
        backgroundColor: presentation.background,
        borderColor: presentation.border,
        color: presentation.text,
      }}
      role="alert"
      aria-label={t('intake.validationStep.missingHeading')}
    >
      <h3 className="mb-1 text-[12px] font-semibold">
        <span aria-hidden="true" className="mr-1">
          {presentation.glyph}
        </span>
        {t('intake.validationStep.missingHeading')}
      </h3>
      <p className="mb-1 text-[12px]">
        {t('intake.validationStep.missingBody', { count: missingFields.length })}
      </p>
      <ul className="flex flex-col gap-1">
        {groups.map((group) => {
          const stepName = t(stepLabelKey(group.step));
          return (
            <li key={group.step} className="text-[12px]">
              <span className="font-medium">{stepName}</span>
              <span className="ml-1 opacity-80">
                {t('validationSummary.affectedFields', {
                  fields: group.paths
                    .map((path) => t(fieldLabelKey(path), { defaultValue: path }))
                    .join(', '),
                })}
              </span>
              {onNavigateToStep ? (
                <Button
                  variant="ghost"
                  size="xs"
                  className="ml-2 underline"
                  onClick={() => onNavigateToStep(group.step)}
                >
                  {t('intake.validationStep.goToStep', { step: stepName })}
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The catalogue key of a step's short label, from the pure step registry. */
function stepLabelKey(step: WizardStepId): string {
  return WIZARD_STEPS.find((candidate) => candidate.id === step)?.labelKey ?? step;
}

/** One stacked tier block (UI spec §3.8), omitted entirely when it has no finding. */
function TierGroup({
  tier,
  findings,
  unacknowledgedWarningRuleIds,
  onAcknowledgeWarning,
}: {
  readonly tier: SummaryTier;
  readonly findings: readonly SummaryFinding[];
  readonly unacknowledgedWarningRuleIds: readonly string[];
  readonly onAcknowledgeWarning: (ruleId: string) => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (findings.length === 0) return null;

  const presentation = TIER_PRESENTATION[tier];
  const unacknowledged = new Set(unacknowledgedWarningRuleIds);
  // A rule may report several findings in one evaluation — one per overlapping
  // Initiative, say — so only exact repeats are collapsed; distinct occurrences
  // all stay, each distinguished by its own detail below.
  const unique = dedupeFindings(findings);

  return (
    <section
      className={`${presentation.className} rounded-[var(--radius-alert,4px)] border p-2`}
      style={{
        backgroundColor: presentation.background,
        borderColor: presentation.border,
        color: presentation.text,
      }}
      aria-label={t(`validationSummary.tier.${tier}`)}
    >
      <h3 className="mb-1 text-[12px] font-semibold">
        <span aria-hidden="true" className="mr-1">
          {presentation.glyph}
        </span>
        {t(`validationSummary.tier.${tier}`)}
      </h3>
      <ul className="flex flex-col gap-1">
        {unique.map((finding) => {
          const isInactiveOwner = finding.messageKey === 'validation.owner.inactive';
          const needsAck = tier === 'warning' && unacknowledged.has(finding.ruleId);
          return (
            // Keyed on the full identity, detail included: several findings of one
            // rule share a ruleId *and* a field list, so the previous
            // `ruleId-fields` key was the same string for every one of them.
            <li key={findingIdentity(finding)} className="text-[12px]">
              <span>{resolveFindingMessage(t, finding)}</span>
              <FindingSubject finding={finding} />
              {finding.fields.length > 0 ? (
                <span className="ml-1 opacity-80">
                  {t('validationSummary.affectedFields', {
                    fields: finding.fields
                      .map((path) => t(fieldLabelKey(path), { defaultValue: path }))
                      .join(', '),
                  })}
                </span>
              ) : null}
              {isInactiveOwner ? (
                <span className="ml-1 rounded-[3px] border border-current px-1 text-[10px] uppercase">
                  {t('validationSummary.inactiveOwnerBadge')}
                </span>
              ) : null}
              {needsAck ? (
                <Button
                  variant="ghost"
                  size="xs"
                  className="ml-2 underline"
                  onClick={() => onAcknowledgeWarning(finding.ruleId)}
                >
                  {t('validationSummary.acknowledge')}
                </Button>
              ) : null}
              {tier === 'warning' && !needsAck ? (
                <span className="ml-2 text-[10px] uppercase opacity-70">
                  {t('validationSummary.acknowledged')}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * What this particular occurrence of a rule is about, when its detail names a
 * subject the message itself does not.
 *
 * A rule that reports once per offending counterpart resolves to the *same*
 * sentence every time — `WV-001`'s administrator-configured text carries no
 * placeholders, so four overlapping Initiatives produced four identical lines and
 * read as one message repeated by mistake. The distinguishing facts were on the
 * finding's `detail` all along and simply never rendered. This names the
 * counterpart so each line says which overlap it is about.
 *
 * Only the counterpart Initiative is surfaced, deliberately. The rest of the
 * detail is raw identifiers (`otherInitiativeId`, `primaryPlatformId`,
 * `sharedAudienceIds`) that mean nothing to a reader and would leak ids into the
 * UI; the reference and the date range are the part a planner can act on. A
 * finding with no such detail renders nothing extra, so every other rule is
 * untouched.
 */
function FindingSubject({ finding }: { readonly finding: SummaryFinding }): React.JSX.Element | null {
  const { t } = useTranslation();
  const detail = finding.detail;
  if (detail === undefined || detail === null) return null;

  const ref = detail['otherInitiativeRef'];
  if (typeof ref !== 'string' || ref.length === 0) return null;

  const start = detail['otherStartDate'];
  const end = detail['otherEndDate'];
  const hasRange = isDateLike(start) && isDateLike(end);

  return (
    <span className="ml-1 font-medium">
      {hasRange
        ? t('validationSummary.overlapSubjectWithRange', {
            ref,
            range: formatDateRange(start, end),
          })
        : t('validationSummary.overlapSubject', { ref })}
    </span>
  );
}

/** Whether a detail value can be handed to the date formatter. */
function isDateLike(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value);
}

/**
 * The catalogue key for a logical field path label. A finding reports affected
 * fields by their logical path (`ownerUserId`, `endDate`, `platforms`, …); the
 * label lives in the catalogue under `validationSummary.field.*`, dot-slugged the
 * same way the discard-dialog field labels are, and falls back to the raw path
 * when no label is catalogued.
 */
function fieldLabelKey(path: string): string {
  return `validationSummary.field.${path.replace(/\./g, '_')}`;
}
